import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, IndianRupee, TrendingUp, PieChart, Sparkles } from "lucide-react";
import CandlestickBg from "./CandlestickBg";

const floats = [
  { Icon: IndianRupee, x: "8%", y: "30%", delay: 0, color: "text-emerald-400" },
  { Icon: TrendingUp, x: "85%", y: "22%", delay: 1.2, color: "text-blue-400" },
  { Icon: PieChart, x: "12%", y: "72%", delay: 0.6, color: "text-indigo-400" },
  { Icon: Sparkles, x: "82%", y: "70%", delay: 1.8, color: "text-amber-400" },
];

export default function Hero() {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden">
      <CandlestickBg />

      {/* Floating finance icons */}
      {floats.map((f, i) => (
        <motion.div
          key={i}
          className={`absolute hidden md:block ${f.color}`}
          style={{ left: f.x, top: f.y }}
          animate={{ y: [0, -20, 0], rotate: [0, 8, 0] }}
          transition={{ duration: 6, repeat: Infinity, delay: f.delay, ease: "easeInOut" }}
        >
          <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center shadow-2xl">
            <f.Icon className="w-7 h-7" strokeWidth={1.5} />
          </div>
        </motion.div>
      ))}

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 items-center z-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-8"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs tracking-widest uppercase font-semibold text-slate-300">
              AMFI Registered · SEBI Compliant
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tighter"
            data-testid="hero-headline"
          >
            Grow Your Wealth with
            <br />
            <span className="gradient-text">Smart Investment</span> Solutions
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            Mutual Funds · SIP · Tax Saving · Wealth Creation · Financial Planning — engineered by AMFI-registered
            advisors and trusted by 12,400+ families across India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a href="#services" className="btn-primary inline-flex items-center justify-center gap-2 group" data-testid="hero-cta-invest">
              Start Investing
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="#contact" className="btn-ghost inline-flex items-center justify-center gap-2" data-testid="hero-cta-consult">
              <PlayCircle className="w-4 h-4" />
              Book Free Consultation
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[
              { v: "₹1850Cr+", l: "Assets Advised" },
              { v: "12.4K+", l: "Investors" },
              { v: "15+ yrs", l: "Experience" },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-2xl font-black text-white">{s.v}</div>
                <div className="text-xs text-slate-500 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Visual card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="lg:col-span-5 hidden lg:block"
        >
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div className="relative">
      <div className="glass-strong rounded-3xl p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs text-slate-400 tracking-widest uppercase">Portfolio Value</div>
            <div className="text-3xl font-black mt-1">₹48,45,230</div>
          </div>
          <div className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-bold">
            +51.4%
          </div>
        </div>
        <MiniChart />
        <div className="grid grid-cols-3 gap-3 mt-5">
          {[
            { l: "Today", v: "+₹18.4K", c: "text-emerald-400" },
            { l: "1Y", v: "+22.3%", c: "text-emerald-400" },
            { l: "3Y CAGR", v: "+18.7%", c: "text-blue-400" },
          ].map((m) => (
            <div key={m.l} className="rounded-xl bg-white/5 p-3 border border-white/5">
              <div className="text-[10px] uppercase text-slate-500 tracking-widest">{m.l}</div>
              <div className={`text-sm font-bold ${m.c} font-mono`}>{m.v}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-2xl glass-strong p-4 hidden xl:flex flex-col justify-between">
        <div className="text-xs text-slate-400">Active SIPs</div>
        <div>
          <div className="text-3xl font-black">4</div>
          <div className="text-xs text-emerald-400">₹45,500/mo</div>
        </div>
      </div>
    </div>
  );
}

function MiniChart() {
  // simple svg line
  const pts = [10, 25, 18, 35, 30, 48, 42, 60, 55, 72, 65, 88];
  const max = 100;
  const w = 320, h = 120;
  const path = pts
    .map((p, i) => `${(i / (pts.length - 1)) * w},${h - (p / max) * h}`)
    .join(" L ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
      <defs>
        <linearGradient id="fillgrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563EB" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M 0,${h} L ${path} L ${w},${h} Z`} fill="url(#fillgrad)" />
      <path d={`M ${path}`} stroke="#60A5FA" strokeWidth="2" fill="none" />
    </svg>
  );
}
